import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ResidentialHeader, useResidentialBasePath } from '../ResidentialHome';

/**
 * Phase 5.E — family-member management in the residential shell.
 *
 * Reuses the existing manager-side ``/api/customer/users`` endpoints
 * (Phase 2.5.B). A residential customer's manager (= the bill-payer)
 * invites family members as viewers; each invitee gets the standard
 * magic-link email and lands as a viewer with full open/lock rights
 * for the household's gates.
 *
 * SMS magic-link is Phase 5.F — for now the invite still goes by
 * email so the workflow is usable today. The endpoint shape doesn't
 * change when 5.F lands; only the channel does.
 */
function ResidentialFamilyPage({ user }) {
  const basePath = useResidentialBasePath();
  const isPreview = basePath !== '/r';
  const [members, setMembers] = useState(isPreview ? MOCK_FAMILY : null);
  const [showInvite, setShowInvite] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const loadMembers = () => {
    if (isPreview) return;
    axios.get('/api/customer/users')
      .then(res => setMembers(res.data))
      .catch(err => setLoadError(err.response?.data?.error || 'שגיאה בטעינה'));
  };

  useEffect(loadMembers, [isPreview]);

  const me = user;
  const familyOnly = (members || []).filter(
    m => m.is_active && m.id !== me?.id,
  );

  return (
    <>
      <ResidentialHeader
        title="בני הבית"
        subtitle={`${familyOnly.length + 1} משתמשים`}
        backTo={`${basePath}/`}
      />
      <div className="r-subpage">
        {isPreview && (
          <div className="r-banner-stub">תצוגה מקדימה — רשימה מדומה.</div>
        )}
        {loadError && (
          <div className="r-banner-stub" style={{ background: '#FEE2E2', borderColor: '#FCA5A5', color: '#991B1B' }}>
            {loadError}
          </div>
        )}

        {/* "Me" card always on top */}
        <MemberRow
          name={me?.name || me?.username}
          email={me?.email}
          isMe
          tag="בעל החשבון"
        />

        {familyOnly.map((m) => (
          <MemberRow
            key={m.id}
            name={m.name}
            email={m.email}
            tag={m.has_logged_in ? 'בן בית' : 'מוזמן — עדיין לא נכנס'}
            tone={m.has_logged_in ? 'ok' : 'pending'}
          />
        ))}

        {members !== null && familyOnly.length === 0 && (
          <div className="r-empty" style={{ marginBottom: '0.75rem' }}>
            <strong>עדיין אין בני בית.</strong><br />
            הזמינו את בני המשפחה כדי שיוכלו גם הם לפתוח את השער מהאפליקציה.
          </div>
        )}

        {!isPreview && (
          <button
            type="button"
            className="r-open-btn"
            style={{ marginTop: '0.75rem' }}
            onClick={() => setShowInvite(true)}
          >
            + הזמינו בן בית
          </button>
        )}
      </div>

      {showInvite && (
        <InviteModal
          onClose={() => setShowInvite(false)}
          onInvited={() => { setShowInvite(false); loadMembers(); }}
        />
      )}
    </>
  );
}

const MOCK_FAMILY = [
  { id: 101, name: 'עילי חורי', email: 'ilai@huri.test', is_active: true, has_logged_in: true },
  { id: 102, name: 'אורית חורי', email: 'orit@huri.test', is_active: true, has_logged_in: false },
];

function MemberRow({ name, email, tag, tone, isMe }) {
  const initials = (name || '?').trim().slice(0, 1);
  const accent = isMe ? '#1B3A5C' : tone === 'pending' ? '#F97316' : '#22C55E';
  const tagStyle = {
    fontSize: '0.7rem',
    fontWeight: 600,
    borderRadius: '999px',
    padding: '0.15rem 0.55rem',
    color: tone === 'pending' ? '#9a3412' : '#1B3A5C',
    background: tone === 'pending' ? '#FFEDD5' : isMe ? '#E0E7FF' : '#DCFCE7',
  };
  return (
    <div className="r-subpage-section" style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
      <div style={{
        width: 44, height: 44, borderRadius: '50%',
        background: accent, color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 700, fontSize: '1.05rem', flexShrink: 0,
      }} aria-hidden="true">
        {initials}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, color: '#1B3A5C' }}>{name || '—'}</div>
        {email && (
          <div style={{ fontSize: '0.82rem', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {email}
          </div>
        )}
      </div>
      <span style={tagStyle}>{tag}</span>
    </div>
  );
}

function InviteModal({ onClose, onInvited }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await axios.post('/api/customer/users', {
        name,
        email,
        template_key: 'household_member',
      });
      onInvited();
    } catch (err) {
      // Fall back to plain viewer if the household_member template
      // isn't recognised by the server (older deploys).
      try {
        await axios.post('/api/customer/users', { name, email });
        onInvited();
        return;
      } catch (err2) {
        setError(err2.response?.data?.error || err.response?.data?.error || 'שגיאה בהזמנה');
        setSubmitting(false);
      }
    }
  };

  return (
    <div className="r-modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="r-modal" onClick={(e) => e.stopPropagation()}>
        <h3>הזמינו בן בית</h3>
        <p>
          בני הבית מקבלים גישה מלאה לפתיחה ולנעילה של השערים. כשהמערכת
          תקבל תמיכת SMS, ההזמנה תעבור גם דרך הודעה לטלפון — בינתיים
          הקישור נשלח במייל.
        </p>
        <form onSubmit={submit} style={{ textAlign: 'start' }}>
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', color: '#64748b', marginBottom: '0.3rem' }}>שם</label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              required
              autoFocus
              placeholder="לדוגמה: אורית חורי"
              style={INPUT_STYLE}
            />
          </div>
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', color: '#64748b', marginBottom: '0.3rem' }}>מייל</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              placeholder="orit@example.com"
              style={INPUT_STYLE}
            />
          </div>
          {error && (
            <div style={{
              background: '#FEE2E2', color: '#991B1B',
              padding: '0.6rem 0.85rem', borderRadius: '10px',
              fontSize: '0.85rem', marginBottom: '0.75rem',
            }}>{error}</div>
          )}
          <div className="r-modal-actions">
            <button type="button" className="r-btn r-btn-secondary" onClick={onClose}>
              ביטול
            </button>
            <button type="submit" className="r-btn r-btn-primary" disabled={submitting}>
              {submitting ? 'שולח…' : 'שלחו הזמנה'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const INPUT_STYLE = {
  width: '100%',
  padding: '0.75rem 0.85rem',
  border: '1px solid #E2E8F0',
  borderRadius: '10px',
  fontSize: '0.95rem',
  fontFamily: 'inherit',
  background: '#F8FAFC',
  color: '#1B3A5C',
  direction: 'rtl',
};

export default ResidentialFamilyPage;
